// Screen routes (rebuild.md §4.9).
import type { ComponentType } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Archive } from '@screens/Archive.tsx';
import { Chapters } from '@screens/Chapters.tsx';
import { Credits } from '@screens/Credits.tsx';
import { Home } from '@screens/Home.tsx';
import { Settings } from '@screens/Settings.tsx';
import { Theatre } from '@screens/Theatre.tsx';

/** The Notebook carries the whole glossary and every stage's entry: its own chunk, off the Home budget (§9.15). */
function Notebook() {
  const [C, setC] = useState<ComponentType | null>(null);
  useEffect(() => {
    let alive = true;
    void import('@screens/Notebook.tsx').then((m) => alive && setC(() => m.Notebook));
    return () => {
      alive = false;
    };
  }, []);
  return C ? <C /> : <main id="main" class="screen" aria-busy="true" />;
}

/** The ending screens (end card, ECHO's upgrade allocation): their own chunk. */
function lazyEnd(name: 'End' | 'EchoUpgrade'): ComponentType {
  return function LazyEnd() {
    const [C, setC] = useState<ComponentType | null>(null);
    useEffect(() => {
      let alive = true;
      void import('@screens/End.tsx').then((m) => alive && setC(() => m[name]));
      return () => {
        alive = false;
      };
    }, []);
    return C ? <C /> : <main id="main" class="screen" aria-busy="true" />;
  };
}
/** A lazily loaded screen that takes route params (drills, Monday sets, Lab Practice): each its own chunk. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyScreen<K extends string>(load: () => Promise<Record<K, ComponentType<any>>>, name: K): ComponentType<{ params: Record<string, string> }> {
  return function LazyScreen(props: { params: Record<string, string> }) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [C, setC] = useState<ComponentType<any> | null>(null);
    useEffect(() => {
      let alive = true;
      void load().then((m) => alive && setC(() => m[name]));
      return () => {
        alive = false;
      };
    }, []);
    return C ? <C {...props} /> : <main id="main" class="screen" aria-busy="true" />;
  };
}
const Drill = lazyScreen(() => import('@screens/Drill.tsx'), 'Drill');
const Monday = lazyScreen(() => import('@screens/Monday.tsx'), 'Monday');
const MondaySet = lazyScreen(() => import('@screens/Monday.tsx'), 'MondaySet');
const Practice = lazyScreen(() => import('@screens/Practice.tsx'), 'Practice');
const PracticeRun = lazyScreen(() => import('@screens/PracticeRun.tsx'), 'PracticeRun');

const End = lazyEnd('End');
const EchoUpgrade = lazyEnd('EchoUpgrade');

export const ROUTES: {
  Home: ComponentType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routes: { path: string; component: ComponentType<any> }[];
} = {
  Home,
  routes: [
    { path: '/chapters', component: Chapters },
    { path: '/theatre', component: Theatre },
    { path: '/archive', component: Archive },
    { path: '/notebook', component: Notebook },
    { path: '/settings', component: Settings },
    { path: '/credits', component: Credits },
    { path: '/end', component: End },
    { path: '/monday', component: Monday },
    { path: '/monday/:id', component: MondaySet },
    { path: '/echo-upgrade', component: EchoUpgrade },
    { path: '/drill/:id', component: Drill },
    { path: '/practice', component: Practice },
    { path: '/practice/:id', component: PracticeRun },
  ],
};
